/**
 * Display helpers for stock replenishment and the stock cutover (BrandHub
 * doc 09 / 10): labels for the backend's enums, the "why is it waiting"
 * line, and the trigger arithmetic. Pure and headless; the decisions
 * themselves (mode defaults, refusals) are the backend's.
 */

import type { ReplenishmentStageFilter } from "../data/adapter";
import { formatCalendarWeekdayDateOrDash } from "./formatters";
import type {
  ArrivalConfidence,
  ReplenishmentAdviseReason,
  ReplenishmentFailureCode,
  ReplenishmentProgress,
  ReplenishmentStage,
  ReplenishmentMode,
  ReplenishmentModeSource,
  ReplenishmentOrderKind,
  ReplenishmentPriceSource,
  ReplenishmentProposalItem,
  ReplenishmentProposalState,
  ReplenishmentSpecStatus,
  StockOwnerCutoverState,
} from "../data/api-types";

export const REPLENISHMENT_MODE_LABELS: Record<ReplenishmentMode, string> = {
  automatic: "Automatic",
  advise: "Advise",
};

export const REPLENISHMENT_MODE_SOURCE_LABELS: Record<
  ReplenishmentModeSource,
  string
> = {
  default: "default from the owner",
  set_by_person: "set by a person",
};

export const SPEC_STATUS_LABELS: Record<ReplenishmentSpecStatus, string> = {
  mapped: "Mapped",
  spec_needed: "Spec needed",
};

export const PROPOSAL_STATE_LABELS: Record<ReplenishmentProposalState, string> =
  {
    open: "Open",
    raised: "Raised",
    dismissed: "Dismissed",
    failed: "Failed",
  };

export const RAISES_AS_LABELS: Record<ReplenishmentOrderKind, string> = {
  customer_order: "Customer order",
  internal_stock_order: "Internal stock order (Pollards' own account)",
};

export const CUTOVER_STATE_LABELS: Record<StockOwnerCutoverState, string> = {
  shadow: "Shadow",
  cut_over: "Cut over",
  excluded: "Excluded",
};

/** Why an advise proposal waits, and what a person does about it. */
export const ADVISE_REASONS: Record<
  ReplenishmentAdviseReason,
  { label: string; hint: string }
> = {
  policy_is_advise: {
    label: "Advise policy",
    hint: "The policy asks a person to confirm each reprint. Check the quantity and confirm, or dismiss it.",
  },
  artwork_needed: {
    label: "No approved artwork",
    hint: "The catalogue item has no approved revision. Supply and approve a revision on the catalogue item, then confirm.",
  },
  artwork_not_valid: {
    label: "Artwork expired or expiring",
    hint: "The current revision has expired or expires inside the margin. Supply a new revision on the catalogue item, or confirm if the customer agrees to reprint it as is.",
  },
  spec_needed: {
    label: "Spec needed",
    hint: "The stock item isn't linked to a repeatable catalogue item, so One can't specify the reprint. Map the stock item to a catalogue item. Until then, One won't reprint the stock item.",
  },
  no_supplier_option: {
    label: "No supplier option",
    hint: "No supplier route offers this reprint at this quantity within the time allowed. Change the quantity, loosen 'needed within', pin another route, or ask for the supplier's cost to be cached.",
  },
  supplier_pricing_hidden: {
    label: "Supplier pricing hidden",
    hint: "Supplier pricing is hidden in Settings, so nothing can be sourced.",
  },
  price_needed: {
    label: "Price needed",
    hint: "The owner has no usable price from a price group. Type the price to bill, then confirm.",
  },
  po_number_needed: {
    label: "Order number needed",
    hint: "The owner's account needs a customer order number before an order is raised. Enter it, then confirm.",
  },
  payment_not_arranged: {
    label: "No credit account",
    hint: "The owner has no credit account, so a bill-and-hold order can't be raised. Set the account up in credit control, or raise the reprint by hand.",
  },
};

/** What a failed raise says went wrong. */
export const FAILURE_LABELS: Record<ReplenishmentFailureCode, string> = {
  own_account_missing: "Pollards' own account isn't set up",
  order_refused: "Orders refused the order",
  sourcing_unavailable: "Supplier options could not be read",
};

/** Where a line's price came from. */
export const PRICE_SOURCE_LABELS: Record<ReplenishmentPriceSource, string> = {
  price_group: "from the owner's price group",
  typed_by_person: "typed by a person",
  at_cost: "at cost (an internal order is never invoiced)",
};

/**
 * A price typed in pounds ("545", "545.5", "1,045.00", "£545.00") as pence, or null when it is not a price: more
 * than two decimals, not a number, or not above zero. The backend decides whether a price is wanted and its limits.
 */
export function parsePoundsToPence(text: string): number | null {
  const cleaned = text.trim().replace(/^£/, "").replace(/,/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const pence = Math.round(Number(cleaned) * 100);
  return pence > 0 ? pence : null;
}

/** Where a raised order's line has got to. */
export const STAGE_LABELS: Record<ReplenishmentStage, string> = {
  waiting_on_credit: "Waiting on credit",
  waiting_to_release: "Waiting to release",
  to_place: "To place",
  placed: "Placed",
  despatched: "Despatched",
  arrived_not_in_stock: "Arrived, not in stock",
  in_stock: "In stock",
  cancelled: "Cancelled",
};

/** The badge colour of each stage: a stage that needs a person is a warning. */
export const STAGE_BADGES: Record<
  ReplenishmentStage,
  "warning" | "success" | "neutral" | "error"
> = {
  waiting_on_credit: "warning",
  waiting_to_release: "neutral",
  to_place: "warning",
  placed: "neutral",
  despatched: "neutral",
  arrived_not_in_stock: "warning",
  in_stock: "success",
  cancelled: "error",
};

export const STAGE_FILTER_LABELS: Record<ReplenishmentStageFilter, string> = {
  still_coming: "Still coming",
  waiting_to_place: "Waiting to place",
  arrived_not_in_stock: "Arrived, not in stock",
  done: "Done",
};

/** "about Wed 21 Oct (assumed)": an expected arrival with how far to trust it; "No date yet" with none. */
export function expectedArrivalLabel(
  on: string | null,
  confidence: ArrivalConfidence | null,
): string {
  if (on == null) return "No date yet";
  const date = formatCalendarWeekdayDateOrDash(on);
  return confidence === "confirmed" ? date : `about ${date}`;
}

/** A batch's unit cost, which is in pence with fractions of one: 8.24 as "£0.0824", 120 as "£1.20". */
export function formatUnitCostPence(pence: number): string {
  const pounds = (pence / 100).toFixed(4).replace(/0{1,2}$/, "");
  return `£${pounds}`;
}

/** The booked-in batch as the Progress card words it: "Batch 14 · unit cost £0.0824"; null when there is none. */
export function batchLabelWithCost(
  progress: Pick<ReplenishmentProgress, "batch_no" | "unit_cost_pence">,
): string | null {
  if (progress.batch_no == null) return null;
  return progress.unit_cost_pence == null
    ? `Batch ${progress.batch_no}`
    : `Batch ${progress.batch_no} · unit cost ${formatUnitCostPence(progress.unit_cost_pence)}`;
}

/** One line of a raised order's Progress card. */
export interface ProgressStep {
  key: string;
  /** done: happened. now: where it is. problem: stuck and needs a person. */
  state: "done" | "now" | "problem";
  title: string;
  detail: string | null;
  /** A calendar date or timestamp the step happened or is due; null for none. */
  when: string | null;
}

const lineNumber = (lineRef: string) => `.${lineRef.split(".").pop() ?? ""}`;

/**
 * The steps of a raised order's progress, from the read's facts. The stage says where it is; the steps before it
 * are done, the stage's own is "now" (or "problem" when stock owes a booking), and none after it is listed.
 */
export function progressSteps(
  progress: ReplenishmentProgress,
  raised: { at: string | null; byName: string | null },
  formatCount: (n: number) => string,
): ProgressStep[] {
  const steps: ProgressStep[] = [
    {
      key: "raised",
      state: "done",
      title: "Raised",
      detail: `Order ${progress.order_no}, line ${lineNumber(progress.line_ref)}, ${formatCount(progress.quantity)} to ${progress.warehouse_name ?? "the warehouse"}${raised.byName ? `, by ${raised.byName}` : ""}`,
      when: raised.at,
    },
  ];
  const stage = progress.stage;
  if (stage === "cancelled") {
    steps.push({
      key: "cancelled",
      state: "problem",
      title: "Cancelled",
      detail:
        "The order line was cancelled; the next sweep proposes again if the item is still low.",
      when: null,
    });
    return steps;
  }
  if (stage === "waiting_on_credit") {
    steps.push({
      key: "credit",
      state: "now",
      title: "Waiting on credit",
      detail: "Finance decides; the line cannot release until then",
      when: null,
    });
    return steps;
  }
  if (stage === "waiting_to_release") {
    steps.push({
      key: "release",
      state: "now",
      title: "Waiting to release",
      detail: "The order releases when every check on it passes",
      when: null,
    });
    return steps;
  }
  steps.push({
    key: "released",
    state: "done",
    title: "Released",
    detail: null,
    when: progress.released_at,
  });
  if (stage === "to_place") {
    steps.push({
      key: "to_place",
      state: "now",
      title: "To place",
      detail: progress.purchase_order_no
        ? `${progress.purchase_order_no} is waiting for Purchasing to place it with the supplier`
        : "Waiting for Purchasing to place it with the supplier",
      when: null,
    });
    return steps;
  }
  steps.push({
    key: "placed",
    state: "done",
    title: progress.supplier_name
      ? `Placed with ${progress.supplier_name}`
      : "Placed with the supplier",
    detail: progress.purchase_order_no,
    when: null,
  });
  if (stage === "placed") {
    steps.push({
      key: "despatch",
      state: "now",
      title: "Despatch",
      detail: `Arrival expected: ${expectedArrivalLabel(progress.expected_arrival_on, progress.expected_arrival_confidence)}`,
      when: null,
    });
    return steps;
  }
  steps.push({
    key: "despatched",
    state: stage === "despatched" ? "now" : "done",
    title: "Despatched",
    detail:
      [
        progress.carrier ? `Courier ${progress.carrier}` : null,
        progress.tracking_reference
          ? `tracking ${progress.tracking_reference}`
          : null,
      ]
        .filter(Boolean)
        .join(", ") || null,
    when: progress.despatched_on,
  });
  if (stage === "despatched") {
    steps[steps.length - 1]!.detail = [
      steps[steps.length - 1]!.detail,
      `arrival expected ${expectedArrivalLabel(progress.expected_arrival_on, progress.expected_arrival_confidence)}`,
    ]
      .filter(Boolean)
      .join(" · ");
    return steps;
  }
  steps.push({
    key: "arrived",
    state: "done",
    title: "Arrived",
    detail: `${formatCount(progress.received_quantity)} recorded received${progress.received_by_name ? ` by ${progress.received_by_name}` : ""}`,
    when: progress.arrived_on,
  });
  steps.push(
    stage === "in_stock"
      ? {
          key: "in_stock",
          state: "done",
          title: "In stock",
          detail: batchLabelWithCost(progress),
          when: progress.booked_at,
        }
      : {
          key: "in_stock",
          state: "problem",
          title: "In stock",
          detail:
            progress.booking_owed_since != null
              ? `Booking owed, attempt ${progress.book_in_attempts + 1}`
              : "Stock has not booked it in yet",
          when: null,
        },
  );
  return steps;
}

/** The list's "why it's waiting" cell: the advise reason, or what a raised one did. */
export function proposalWaitingLabel(
  proposal: Pick<
    ReplenishmentProposalItem,
    "state" | "advise_reason" | "raised_kind" | "last_failure_code"
  >,
): string {
  if (proposal.state === "raised") {
    return proposal.raised_kind === "dry_run" ? "Dry run raised" : "Raised";
  }
  if (proposal.state === "failed") {
    return proposal.last_failure_code
      ? FAILURE_LABELS[proposal.last_failure_code]
      : "Raise failed";
  }
  if (proposal.state === "dismissed") return "Dismissed";
  return proposal.advise_reason
    ? ADVISE_REASONS[proposal.advise_reason].label
    : "Waiting to raise";
}

/** "on hand 420 − picked 0 = projected 420 ≤ level 500". */
export function triggerArithmetic(
  proposal: Pick<
    ReplenishmentProposalItem,
    "on_hand" | "open_picks" | "on_order" | "projected" | "reorder_level"
  >,
  formatCount: (n: number) => string,
): string {
  const onOrder =
    proposal.on_order > 0
      ? ` + on order ${formatCount(proposal.on_order)}`
      : "";
  const level =
    proposal.reorder_level == null
      ? ""
      : ` ≤ level ${formatCount(proposal.reorder_level)}`;
  return `on hand ${formatCount(proposal.on_hand)} − picked ${formatCount(proposal.open_picks)}${onOrder} = projected ${formatCount(proposal.projected)}${level}`;
}
